/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type NavigateFunction } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  type IntelligenceData,
  IntelligenceStatus,
  IntelligenceType,
} from '@coze-arch/idl/intelligence_api';

export function getQuickOpenShortcutLabel(): string {
  if (typeof navigator === 'undefined') {
    return '⌘K';
  }
  return /Mac|iPhone|iPod|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl+K';
}

export function navigateToIntelligence(
  navigate: NavigateFunction,
  item: IntelligenceData,
): void {
  const { id, space_id, status } = item.basic_info ?? {};
  if (!id || !space_id || status === IntelligenceStatus.Banned) {
    return;
  }

  if (item.type === IntelligenceType.Bot) {
    navigate(`/space/${space_id}/bot/${id}`);
    return;
  }

  if (item.type === IntelligenceType.Project) {
    navigate(`/space/${space_id}/project-ide/${id}`);
  }
}

export function filterQuickOpenItems(
  items: IntelligenceData[],
  query: string,
): IntelligenceData[] {
  const keyword = query.trim().toLowerCase();
  if (!keyword) {
    return items;
  }

  return items.filter(item => {
    const name = item.basic_info?.name?.toLowerCase() ?? '';
    const description = item.basic_info?.description?.toLowerCase() ?? '';
    return name.includes(keyword) || description.includes(keyword);
  });
}

export function useQuickOpenShortcut(onToggle: () => void): void {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onToggle();
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onToggle]);
}

export function useQuickOpen() {
  const [open, setOpen] = useState(false);
  const shortcutLabel = useMemo(() => getQuickOpenShortcutLabel(), []);

  const openPalette = useCallback(() => setOpen(true), []);
  const closePalette = useCallback(() => setOpen(false), []);
  const togglePalette = useCallback(() => setOpen(prev => !prev), []);

  useQuickOpenShortcut(togglePalette);

  return {
    open,
    shortcutLabel,
    openPalette,
    closePalette,
    togglePalette,
  };
}
